/**
 * The one pass that keeps a scene self-consistent.
 *
 * It runs once at the end of a reducer action, never per pointer move and never
 * per element mutation. Two properties are load bearing:
 *
 *  - It is idempotent. Running it twice returns the identical objects.
 *  - It bumps `version` only on elements it actually changed. A version bumped
 *    on a no-op would be broadcast to the other tab, which would normalize,
 *    bump, and broadcast back without end.
 */

import {
  CONTAINER_PADDING,
  fontSpecFor,
  measureTextBlock,
  textLinesFor,
  type MeasureText,
} from '../geometry/drawable';
import { polylinesOf } from '../geometry/cache';
import {
  add,
  centerOf,
  normalize as unitVec,
  rotateVec,
  segmentIntersection,
  sub,
  toLocal,
  toWorld,
  type Vec,
} from '../geometry/transform';
import { updateElement, type EditMeta } from './elements';
import type { ArrowElement, BoxElement, Point, SlateElement, TextElement } from './types';
import { isArrow, isBindable, isLinear, isText } from './types';

const EPSILON = 1e-6;

export interface NormalizeOptions {
  /** Ids whose geometry changed. Undefined means a full pass. */
  readonly dirty?: ReadonlySet<string>;
  readonly measureText: MeasureText;
  readonly meta: EditMeta;
  /** False for passes triggered by a remote message, so versions stay as sent. */
  readonly bumpVersions?: boolean;
}

function samePoints(a: readonly Point[], b: readonly Point[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (Math.abs(a[i]![0] - b[i]![0]) > EPSILON) return false;
    if (Math.abs(a[i]![1] - b[i]![1]) > EPSILON) return false;
  }
  return true;
}

/**
 * New x/y for an element resized so that `anchorOld` (a local point in the old
 * box) stays put in world space and becomes `anchorNew` in the new box.
 */
function anchoredBox(
  el: SlateElement,
  anchorOld: Vec,
  anchorNew: Vec,
  width: number,
  height: number,
): { x: number; y: number; width: number; height: number } {
  const anchorWorld = toWorld(anchorOld, el);
  const center = add(
    anchorWorld,
    rotateVec({ x: width / 2 - anchorNew.x, y: height / 2 - anchorNew.y }, el.angle),
  );
  return { x: center.x - width / 2, y: center.y - height / 2, width, height };
}

/**
 * Where a segment coming from `fromWorld` crosses the target's border, plus the
 * outward direction there. Uses the same flattened polyline the renderer draws,
 * so rect, ellipse, diamond and every future shape share one implementation.
 *
 * This is an intersection, not a nearest point: nearest-point-on-border looks
 * visibly wrong on wide rectangles.
 */
function borderCrossing(
  target: SlateElement,
  fromWorld: Vec,
  measure: MeasureText,
): { world: Vec; outward: Vec } | null {
  const centerLocal = { x: target.width / 2, y: target.height / 2 };
  const fromLocal = toLocal(fromWorld, target);
  let best: { point: Vec; t: number } | null = null;
  for (const poly of polylinesOf(target, measure)) {
    const pts = poly.points;
    for (let i = 1; i < pts.length; i++) {
      const hit = segmentIntersection(fromLocal, centerLocal, pts[i - 1]!, pts[i]!);
      // Smallest t is the crossing nearest the incoming end.
      if (hit && (best === null || hit.t < best.t)) best = hit;
    }
  }
  if (!best) return null;
  const outward = unitVec(sub(best.point, centerLocal));
  if (outward.x === 0 && outward.y === 0) return null;
  return { world: toWorld(best.point, target), outward: rotateVec(outward, target.angle) };
}

function resolveEndpoint(
  target: SlateElement,
  neighborWorld: Vec,
  gap: number,
  measure: MeasureText,
): Vec {
  const crossing = borderCrossing(target, neighborWorld, measure);
  // No crossing means the neighbour is inside the shape (or the shape is
  // degenerate). Clamping to the centre keeps the arrow sane instead of NaN.
  if (!crossing) return centerOf(target);
  const distance = Math.hypot(
    neighborWorld.x - crossing.world.x,
    neighborWorld.y - crossing.world.y,
  );
  // Clamp the gap so two adjacent shapes cannot push the arrow inside out.
  const effective = Math.max(0, Math.min(gap, distance / 2));
  return add(crossing.world, {
    x: crossing.outward.x * effective,
    y: crossing.outward.y * effective,
  });
}

function bindingTarget(
  arrow: ArrowElement,
  which: 'start' | 'end',
  byId: ReadonlyMap<string, SlateElement>,
): BoxElement | null {
  const binding = which === 'start' ? arrow.startBinding : arrow.endBinding;
  if (!binding) return null;
  // Self-loops are rejected: the centre-to-centre direction is the zero vector,
  // and the resulting NaN would spread through the whole scene.
  if (arrow.startBinding && arrow.endBinding && arrow.startBinding.elementId === arrow.endBinding.elementId) {
    return null;
  }
  const target = byId.get(binding.elementId);
  // A missing or deleted target freezes the endpoint where it was. Bindings are
  // never cleared on delete, so undo can restore the connection.
  if (!target || target.isDeleted || !isBindable(target)) return null;
  return target;
}

function resolveArrowPoints(
  arrow: ArrowElement,
  byId: ReadonlyMap<string, SlateElement>,
  measure: MeasureText,
): readonly Point[] {
  const pts = arrow.points;
  if (pts.length < 2) return pts;
  const next = pts.slice();

  const startTarget = bindingTarget(arrow, 'start', byId);
  const endTarget = bindingTarget(arrow, 'end', byId);
  if (!startTarget && !endTarget) return pts;

  // Direction comes from the ADJACENT point, not the far endpoint: with three or
  // more points the far end says nothing useful about this end.
  if (startTarget) {
    const neighbor = next[1]!;
    const neighborWorld = toWorld({ x: neighbor[0], y: neighbor[1] }, arrow);
    const world = resolveEndpoint(startTarget, neighborWorld, arrow.startBinding!.gap, measure);
    const local = toLocal(world, arrow);
    next[0] = [local.x, local.y];
  }
  if (endTarget) {
    const neighbor = next[next.length - 2]!;
    const neighborWorld = toWorld({ x: neighbor[0], y: neighbor[1] }, arrow);
    const world = resolveEndpoint(endTarget, neighborWorld, arrow.endBinding!.gap, measure);
    const local = toLocal(world, arrow);
    next[next.length - 1] = [local.x, local.y];
  }
  return samePoints(next, pts) ? pts : next;
}

/** Shift points so their bounding box starts at (0,0), moving x/y to match. */
function reoriginPoints(
  el: SlateElement & { points: readonly Point[] },
): { x: number; y: number; width: number; height: number; points: readonly Point[] } | null {
  // Only safe at angle 0: moving x/y moves the rotation centre.
  if (el.angle !== 0 || el.points.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [px, py] of el.points) {
    minX = Math.min(minX, px);
    minY = Math.min(minY, py);
    maxX = Math.max(maxX, px);
    maxY = Math.max(maxY, py);
  }
  const width = maxX - minX;
  const height = maxY - minY;
  if (Math.abs(minX) < EPSILON && Math.abs(minY) < EPSILON) {
    if (Math.abs(el.width - width) < EPSILON && Math.abs(el.height - height) < EPSILON) return null;
    return { x: el.x, y: el.y, width, height, points: el.points };
  }
  const points = el.points.map(([px, py]) => [px - minX, py - minY] as Point);
  return { x: el.x + minX, y: el.y + minY, width, height, points };
}

export function normalizeScene(
  elements: readonly SlateElement[],
  opts: NormalizeOptions,
): readonly SlateElement[] {
  const { measureText, meta, dirty } = opts;
  const bump = opts.bumpVersions !== false;
  const byId = new Map<string, SlateElement>();
  for (const el of elements) byId.set(el.id, el);

  const isDirty = (id: string | null | undefined): boolean =>
    dirty === undefined || (id !== null && id !== undefined && dirty.has(id));

  const apply = <T extends SlateElement>(el: T, patch: Partial<T>): T => {
    if (bump) return updateElement(el, patch, meta);
    const merged = { ...el, ...patch } as T;
    return Object.freeze(merged);
  };

  let out = elements;
  const replace = (index: number, next: SlateElement) => {
    if (next === out[index]) return;
    if (out === elements) out = elements.slice();
    (out as SlateElement[])[index] = next;
    byId.set(next.id, next);
  };

  // 1. Non-negative sizes. Defensive: the reducer normalizes before this point.
  for (let i = 0; i < out.length; i++) {
    const el = out[i]!;
    if (el.width < 0 || el.height < 0) {
      replace(i, apply(el, { width: Math.abs(el.width), height: Math.abs(el.height) } as Partial<SlateElement>));
    }
  }

  // 2. Bound text: cascade deletion, re-wrap, re-centre, grow the container.
  for (let i = 0; i < out.length; i++) {
    const el = out[i]!;
    if (!isText(el)) continue;
    const text = el as TextElement;

    if (text.containerId) {
      const container = byId.get(text.containerId);
      if (!container || container.isDeleted) {
        if (!text.isDeleted) replace(i, apply(text, { isDeleted: true }));
        continue;
      }
      if (text.isDeleted) continue;
      if (!isDirty(text.id) && !isDirty(container.id)) continue;

      const font = fontSpecFor(text.fontFamily, text.fontSize);
      const innerWidth = Math.max(font.size, container.width - CONTAINER_PADDING * 2);
      const probe = { ...text, width: innerWidth } as TextElement;
      const lines = textLinesFor(probe, measureText);
      const block = measureTextBlock(lines, font, measureText);

      // Grow the container downward if the label no longer fits, holding its top
      // edge fixed in world space so a rotated container does not walk.
      const neededHeight = block.height + CONTAINER_PADDING * 2;
      let live = container;
      if (neededHeight > container.height + EPSILON) {
        const containerIndex = out.indexOf(container);
        const grown = apply(
          container,
          anchoredBox(
            container,
            { x: container.width / 2, y: 0 },
            { x: container.width / 2, y: 0 },
            container.width,
            neededHeight,
          ) as Partial<SlateElement>,
        );
        if (containerIndex >= 0) replace(containerIndex, grown);
        live = grown;
      }

      // The label is centred both ways, so its centre is the container's centre.
      const center = centerOf(live);
      replace(
        i,
        apply(text, {
          width: innerWidth,
          height: block.height,
          x: center.x - innerWidth / 2,
          y: center.y - block.height / 2,
          angle: live.angle,
          textAlign: 'center',
        } as Partial<TextElement>),
      );
      continue;
    }

    // Standalone text auto-sizes to its content, holding its top-left corner.
    if (text.isDeleted || !isDirty(text.id)) continue;
    const font = fontSpecFor(text.fontFamily, text.fontSize);
    const lines = textLinesFor(text, measureText);
    const block = measureTextBlock(lines, font, measureText);
    if (
      Math.abs(block.width - text.width) > EPSILON ||
      Math.abs(block.height - text.height) > EPSILON
    ) {
      replace(
        i,
        apply(
          text,
          anchoredBox(text, { x: 0, y: 0 }, { x: 0, y: 0 }, block.width, block.height) as Partial<TextElement>,
        ),
      );
    }
  }

  // 3. Arrow bindings, then 4. linear bounding boxes re-derived from points.
  for (let i = 0; i < out.length; i++) {
    const el = out[i]!;
    if (el.isDeleted) continue;

    if (isArrow(el)) {
      const arrow = el as ArrowElement;
      const targetsDirty =
        isDirty(arrow.id) ||
        isDirty(arrow.startBinding?.elementId) ||
        isDirty(arrow.endBinding?.elementId);
      if (targetsDirty) {
        const points = resolveArrowPoints(arrow, byId, measureText);
        if (points !== arrow.points) {
          replace(i, apply(arrow, { points } as Partial<ArrowElement>));
        }
      }
    }

    const current = out[i]!;
    if (!isLinear(current) || !isDirty(current.id)) continue;
    const reoriented = reoriginPoints(current);
    if (reoriented) replace(i, apply(current, reoriented as Partial<SlateElement>));
  }

  return out;
}

/** container id -> its bound text element. Derived, never stored. */
export function boundTextMap(elements: readonly SlateElement[]): Map<string, TextElement> {
  const map = new Map<string, TextElement>();
  for (const el of elements) {
    if (isText(el) && el.containerId && !el.isDeleted) map.set(el.containerId, el);
  }
  return map;
}
